// Mirror of CommonPdfExperiencesSection.jsx: per experience, a 16% / 84% row.
// Left = employer / activity sector / location / dates / duration.
// Right = job position (+ consulting mission), contract type, then optional
// roles, context, achievements, actions and technical environment.

import { TableRow, VerticalAlign } from "docx";
import { parseISO } from "date-fns";
import { calculateDatesDifference } from "../../../../utils/calculateDatesDifference";
import { formatDate } from "../../../../utils/formatDate";
import { bulletLine, cell, dividerRule, frac, layoutTable, para, run } from "../../shared/wordHelpers";
import { FONT_ARIAL } from "../../shared/wordTheme";
import { pxToTwips } from "../../shared/wordUnits";

function durationText( from, to, general = {} ) {
    const { years, months } = calculateDatesDifference( from, to );
    let str = "";
    if ( years > 0 ) {
        str += `${ years } ${ years > 1 ? general.years : general.year }`;
    }
    if ( months > 0 && years > 0 ) {
        str += ` ${ general.and } `;
    }
    if ( months > 0 ) {
        str += `${ months } ${ months > 1 ? general.months : general.month }`;
    }
    return str;
}

function leftColumn( experience, props, t, documentLanguage ) {
    const { isAnonymous } = props;
    const children = [];

    children.push( para( run( isAnonymous ? t.employerLabel : experience.employer, { pt: 8, bold: true, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );

    const sector = experience.activitySector?.translations?.[ documentLanguage ];
    if ( sector ) {
        children.push( para( run( sector, { pt: 8, bold: true, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
    }
    if ( experience.location ) {
        children.push( para( run( experience.location, { pt: 8, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
    }

    const dateText = experience.from && ( experience.to || experience.isCurrent )
        ? `${ formatDate( experience.from, documentLanguage ) } - ${ experience.isCurrent ? t.isCurrentLabel : formatDate( experience.to, documentLanguage ) }`
        : formatDate( experience.from, documentLanguage );
    children.push( para( run( dateText, { pt: 9, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );

    const diff = durationText( experience.from, experience.isCurrent ? null : experience.to, t.general );
    if ( diff ) {
        children.push( para( run( `(${ diff })`, { pt: 9, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
    }
    return children;
}

function labelledList( label, items, accent ) {
    const out = [ para( run( label, { pt: 8, color: accent, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) ];
    items
        .filter( item => item?.text && item.text.trim() !== "" )
        .sort( ( a, b ) => a.order - b.order )
        .forEach( item => out.push( bulletLine( item.text, { pt: 8, font: FONT_ARIAL } ) ) );
    return out;
}

function labelledText( label, text, accent ) {
    return [
        para( run( label, { pt: 8, color: accent, font: FONT_ARIAL } ), { spacing: { after: 0 } } ),
        para( run( text, { pt: 8, font: FONT_ARIAL } ), { spacing: { after: 0 } } ),
    ];
}

function rightColumn( experience, props, t, documentLanguage, accent ) {
    const { isAnonymous } = props;
    const children = [];

    const jobRuns = [ run( `${ experience.jobPosition ?? "" } `, { pt: 8, bold: true, color: accent, font: FONT_ARIAL } ) ];
    if ( experience.isConsultingMission ) {
        const client = isAnonymous && experience.client ? "Client" : experience.client;
        jobRuns.push( run( ` - ${ t.isConsultingMissionLabel } ${ client ?? "" }`, { pt: 8, bold: true, color: accent, font: FONT_ARIAL } ) );
    }
    children.push( para( jobRuns, { spacing: { after: 0 } } ) );

    const contractType = experience.contractType?.translations?.[ documentLanguage ];
    if ( contractType ) {
        children.push( para( run( contractType, { pt: 8, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
    }

    const validRoles = Array.isArray( experience.roles )
        ? experience.roles.filter( role => role?.text && role.text.trim() !== "" )
        : [];
    if ( validRoles.length > 0 ) {
        children.push( ...labelledList( t.rolesLabel, validRoles, accent ) );
    }

    if ( experience.context && experience.context.trim() !== "" ) {
        children.push( ...labelledText( t.contextLabel, experience.context, accent ) );
    }
    if ( experience.achievement && experience.achievement.trim() !== "" ) {
        children.push( ...labelledText( t.achievementsLabel, experience.achievement, accent ) );
    }

    const hasActions = Array.isArray( experience.actions )
        && experience.actions.some( action => action?.text && action.text.trim() !== "" );
    if ( hasActions ) {
        children.push( ...labelledList( t.actionsLabel, experience.actions, accent ) );
    }

    if ( Array.isArray( experience.technicalEnvironment ) && experience.technicalEnvironment.length > 0 ) {
        const techText = experience.technicalEnvironment
            .map( skill => ( typeof skill === "string" ? skill : ( skill?.translations?.[ documentLanguage ] || skill ) ) )
            .join( ", " );
        children.push( para( run( t.technicalEnvironmentLabel, { pt: 8, color: accent, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
        children.push( para( run( techText, { pt: 8, font: FONT_ARIAL } ), { spacing: { after: 0 } } ) );
    }

    return children;
}

export function buildCommonExperiences( { data, props, translations, accent } ) {
    const { experiences } = data;
    const { documentLanguage } = props;
    const t = translations?.pdf ?? {};

    const leftW = frac( 0.16 );
    const rightW = frac( 0.84 );

    const elements = [];

    experiences
        .slice()
        .filter( experience => experience )
        .sort( ( a, b ) => parseISO( b?.from ) - parseISO( a?.from ) )
        .forEach( ( experience, index ) => {
            if ( index > 0 ) {
                elements.push( dividerRule( accent ) );
            }
            elements.push( layoutTable(
                [
                    new TableRow( {
                        children: [
                            cell( leftColumn( experience, props, t, documentLanguage ), { widthTwips: leftW, valign: VerticalAlign.TOP } ),
                            cell( rightColumn( experience, props, t, documentLanguage, accent ), {
                                widthTwips: rightW,
                                valign: VerticalAlign.TOP,
                                margins: { left: pxToTwips( 6 ), top: 0, bottom: 0, right: 0 },
                            } ),
                        ],
                    } ),
                ],
                { columnWidths: [ leftW, rightW ] }
            ) );
        } );

    return elements;
}
